/*
    Author: Sandeep Baynes

    Include this function in the site page and call the showIcon function to display the chat icon
    The options passed to the constructor need to include all the details about client id, context, user access, etc.

    Usage: initMcbs(options).showIcon();
    Where options is of type ChatOptions. See

    The script uses vanilla javascript as using any other framework may cause dependency conflicts on the client page
*/
import { ACTION_AUTHENTICATED, ACTION_CLOSE_MAISON_CHAT, ACTION_CLOSE_QA, ACTION_COPY_QUERY, ACTION_EDIT_CONFIGURATION, ACTION_EDIT_QA, ACTION_EDIT_QA_NEW_TAB, ACTION_QA_EDITOR_READY, ACTION_SIGNOUT, ACTION_TOGGLE_ADMIN_PANEL, ACTION_UPDATE_LOCALE, EVENT_CLIENT_DATA_LOADED, ACTION_UPDATE_SESSION_ID, CHAT_WINDOW, CLIENT_SITE, EVENT_ADMIN_FLYOUT_CLOSED, EVENT_ADMIN_FLYOUT_OPENED, EVENT_ADMIN_WINDOW_OPENED, EVENT_CHAT_ICON_CLICKED, EVENT_CHAT_ICON_INJECTED, EVENT_CHAT_ICON_REMOVED, EVENT_CHAT_ICON_REPOSITIONED, EVENT_CHAT_WINDOW_CLOSED, EVENT_CHAT_WINDOW_OPENED, EVENT_CLIENT_SITE_LOADED, EVENT_EXCEPTION, EVENT_MCBS_INITIALIZED, EVENT_SUGGESTION_MESSAGE_CLOSED, EVENT_SUGGESTION_MESSAGE_DISPLAYED, ACTION_EXECUTE_BOOK_ROOM, ACTION_LOG_ANALYTICS_EVENT } from "./constants";
import { ChatOptions, IconPosition } from "./types/chatOptions";
import { mcbsClosure } from "./types/mcbsClosure";
import { Translations } from "./types/translations";
import { initializeApp } from "firebase/app";
import { logEvent as firebaseLogEvent, getAnalytics, Analytics } from "firebase/analytics";
import { getQueryParam } from './utils';

const chatDomain: string = process.env.CHAT_DOMAIN!;
const qaEditorDomain: string = process.env.QA_EDITOR_DOMAIN!;
const uiRandomInvalidationCode = process.env.UI_VERSION_INVALIDATION_CODE ?? '';
// The env will let any analytics or tracking processes know which env this is. In prod and test, this is left undefined because the analytics project itself is named for the env
// When debugging using the local dev machine, set the env so local machine events can be filtered out in analytics
const env: string | undefined = process.env.ENV;
const analyticsSessionId = getQueryParam('asid') || crypto?.randomUUID(); // Use as a session reference till the new session is returned. This is for analytics tracking when client uses agent but never initializes it.
let analytics: Analytics | undefined;
let logEvent: Function;
let analyticsEventBacklog: any[] = [];
let analyticsProvider: string | undefined = undefined;
// Check if gtag already exists. If this does, then do not log event using firebase, but use window.gtag('event', event_name, params);
let gtagCheckTries = 0;

logEvent = (_analytics: any, event: string, params: any) => {
    analyticsEventBacklog.push({ event, params });
};

/*
MA-729 - When an existing GA or firebase tracker exists on the client site, we need to piggy back on that tracker. Especially if it is legacy GA which uses gtag
there's competition for window.gtag and window.datalayer objects by these google trackers. We need to wait till their root gtag is loaded which can take some time and then init the analytics mechanism based on that.
So loop 20 times with a 100ms interval till you detect gtag. If not detected, then fallback to firebase analytics.
Also keep a backlog of all analytic events till this is intialized. Then flush it all to analytics through the chosen provider.
*/
const initAnalyticsProvider = () => {
    gtagCheckTries++;
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("config", process.env.FIREBASE_MEASUREMENT_ID, { send_page_view: false });
        logEvent = (_analytics: any, event: string, params: any) => {
            window.gtag('event', event, params);
        };
        analyticsProvider = 'gtag';

        // flush queued events
        analyticsEventBacklog.forEach(({ event, params }) => logEvent(analytics, event, params));
        analyticsEventBacklog = [];
    } else if (gtagCheckTries > 20) {
        const firebaseConfig = {
            apiKey: process.env.FIREBASE_API_KEY,
            authDomain: process.env.FIREBASE_AUTH_DOMAIN,
            projectId: process.env.FIREBASE_PROJECT_ID,
            storageBucket: process.env.FIREBASE_STORAGE_BUCKET,
            messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
            appId: process.env.FIREBASE_APP_ID,
            measurementId: process.env.FIREBASE_MEASUREMENT_ID
        };
        const firebaseApp = initializeApp(firebaseConfig, "maison-cx");
        analytics = getAnalytics(firebaseApp);
        logEvent = firebaseLogEvent;
        analyticsProvider = 'firebase';

        // flush queued events
        analyticsEventBacklog.forEach(({ event, params }) => logEvent(analytics, event, params));
        analyticsEventBacklog = [];
    } else {
        setTimeout(initAnalyticsProvider, 100);
    }
};

// Start the provider initialization loop
if (document.readyState == 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
        initAnalyticsProvider();
    });
} else {
    initAnalyticsProvider();
}


logEvent = (analytics: any, event: string, params: any) => {
    if (analyticsProvider == 'gtag') {
        window.gtag('event', event, params);
    }
    else if (analyticsProvider == 'firebase') {
        firebaseLogEvent(analytics, event, params);
    }
    else {
        analyticsEventBacklog.push({ event, params });
    }
}


logEvent(analytics, EVENT_CLIENT_SITE_LOADED, {
    env: env,
    timestamp: Date.now(),
    analyticssessionid: analyticsSessionId
});

// mcbs stands for maison chat bot session
window.initMcbs = function (options: ChatOptions): mcbsClosure {
    if (!!window.mcbs) {
        return window.mcbs;
    }
    /*
        Define constants
    */

    let sessionId = options.sessionId;
    let locale = options.locale;
    const { clientId } = options;
    let qaEditorReady = false;
    let chatClicked = false;
    let translations: Translations;

    // Insert the cwlayout and cwstyle css links to the page
    document.head.insertAdjacentHTML('beforeend', `<link rel="stylesheet" href="${chatDomain}/da/cw/style.bundle.css/?clientid=${options?.clientId ?? ''}&v=${uiRandomInvalidationCode}">`);

    // Insert the chat window and container to the page
    const maisonHtml = `<div id="maison">
        <div id="maison-chat">
            <div id="maison-chat-window">
                <iframe allow-storage-access-by-user-activation allow-same-origin src="${chatDomain}/chat/?clientid=${clientId}&sessionid=${sessionId ?? ''}&locale=${options.locale ?? ''}" frameborder="0"></iframe>
            </div>
        </div>
    </div>`;
    document.body.insertAdjacentHTML('beforeend', maisonHtml);

    logEvent(analytics, EVENT_MCBS_INITIALIZED, {
        env: env,
        timestamp: Date.now(),
        analyticssessionid: analyticsSessionId,
        clientid: options.clientId,
        sessionid: options.sessionId,
        locale: options.locale,
        sessiontoken: options.sessionToken,
        iconposition: options.iconPosition
    });

    if (!clientId) {
        throw new Error('Invalid client id provided for the maison chatbot');
    }

    setTimeout(() => {
        if (chatClicked || !translations.values.invitationMessage || document.getElementById('suggestion-text') == null) {
            return;
        }
        /*HERE*/
        document.getElementById('suggestion-text')!.textContent = (translations?.values.invitationMessage).toString();
        let suggestionElement = document.getElementById('suggestion');
        suggestionElement?.classList.remove('hidden');
        suggestionElement?.classList.add('suggestion-popout');
        logEvent(analytics, EVENT_SUGGESTION_MESSAGE_DISPLAYED, {
            env: env,
            timestamp: Date.now(),
            analyticssessionid: analyticsSessionId,
            sessionid: sessionId,
            clientid: clientId
        });
    }, 2000);

    function onMcbsIconClicked(): void {
        logEvent(analytics, EVENT_CHAT_ICON_CLICKED, {
            env: env,
            timestamp: Date.now(),
            analyticssessionid: analyticsSessionId,
            sessionid: sessionId,
            clientid: clientId
        });
        showChat();
    }

    function showChat(): void {
        document.getElementById('maison-chat-icon')?.classList.remove('visible');
        document.getElementById('maison-chat-window')?.classList.add('visible');
        hideSuggestion();
        chatClicked = true;
        logEvent(analytics, EVENT_CHAT_WINDOW_OPENED, {
            env: env,
            timestamp: Date.now(),
            analyticssessionid: analyticsSessionId,
            sessionid: sessionId,
            clientid: clientId
        });
    }

    function hideChat(options?: { source?: string }): void {
        document.getElementById('maison-chat-window')?.classList.remove('visible');
        document.getElementById('maison-chat-icon')?.classList.add('visible');
        logEvent(analytics, EVENT_CHAT_WINDOW_CLOSED, {
            env: env,
            timestamp: Date.now(),
            analyticssessionid: analyticsSessionId,
            sessionid: sessionId,
            clientid: clientId,
            source: options?.source
        });
    }

    function hideSuggestion(): void {
        if (document.getElementById('suggestion') != null) {
            document.getElementById('suggestion')!.style.display = 'none';
        }
        logEvent(analytics, EVENT_SUGGESTION_MESSAGE_CLOSED, {
            env: env,
            timestamp: Date.now(),
            analyticssessionid: analyticsSessionId,
            sessionid: sessionId,
            clientid: clientId
        });
    }

    function broadcastAuthStatusUpdate(action: string, data?: any): void {
        const message = { action, data };
        const editorElement = document.querySelector<HTMLIFrameElement>('#maison-qa-editor iframe');
        const chatElement = document.querySelector<HTMLIFrameElement>('#maison-chat iframe');
        // If editor is already open, post a message to it
        if (editorElement && data?.source != 'qaeditor') {
            editorElement.contentWindow?.postMessage(JSON.stringify(message), qaEditorDomain);
        }
        // If chat window is available, post the message to it
        if (chatElement && data?.source != 'chatwindow') {
            chatElement.contentWindow?.postMessage(JSON.stringify(message), chatDomain);
        }
    }

    function processChatMessage(event: MessageEvent): void {
        try {
            const { action, data } = JSON.parse(event.data);
            if (action === ACTION_CLOSE_MAISON_CHAT) {
                hideChat({ source: CHAT_WINDOW });
            } else if (action == ACTION_EDIT_QA_NEW_TAB) {
                // Open the QA editor in a new tab
                window.open(`${qaEditorDomain}/qa/tone'/?clientid=${clientId}`, '_blank');
                logEvent(analytics, EVENT_ADMIN_WINDOW_OPENED, {
                    env: env,
                    timestamp: Date.now(),
                    analyticssessionid: analyticsSessionId,
                    sessionid: sessionId,
                    clientid: clientId
                });
            }
            else if (
                action === ACTION_EDIT_QA ||
                action === ACTION_TOGGLE_ADMIN_PANEL ||
                action === ACTION_COPY_QUERY ||
                action === ACTION_EDIT_CONFIGURATION
            ) {
                const editorElement = document.querySelector<HTMLIFrameElement>('#maison-qa-editor iframe');
                // If editor is already open, post a message to it
                if (editorElement && qaEditorReady) {
                    const message = {
                        action: action === ACTION_EDIT_QA ? (data != null ? 'knowledgesearchcontext' : 'knowledgesearchquery') : action === ACTION_COPY_QUERY ? 'copyquery' : action === ACTION_EDIT_CONFIGURATION ? 'openhotelconfiguration' : 'opentonesettings',
                        data,
                    };
                    editorElement.contentWindow?.postMessage(JSON.stringify(message), qaEditorDomain);
                }
                // If not, open the editor window
                else {
                    const trailQueryParam = (data && action === ACTION_EDIT_QA) ? `&contextids=${data}` : (data && action === ACTION_COPY_QUERY) ? `&addquestion=${encodeURIComponent(data.question ?? '')}&addanswer=${encodeURIComponent(data.answer ?? '')}` : '';
                    const htmlContent = `
                    <div id="maison-qa-editor">
                        <iframe allow-storage-access-by-user-activation allow-same-origin src="${qaEditorDomain}/qa/${action === ACTION_TOGGLE_ADMIN_PANEL ? 'tone' : action === ACTION_EDIT_CONFIGURATION ? 'clientconfiguration' : 'knowledge'}/?clientid=${clientId}${trailQueryParam}"></iframe>
                    </div>`;
                    let maisonContainer = document.getElementById("maison");
                    if (!maisonContainer) {
                        const maisonContainerElement = `<div id="maison"></div>`;
                        document.body.insertAdjacentHTML('beforeend', maisonContainerElement);
                    }
                    maisonContainer = document.getElementById("maison");
                    maisonContainer!.insertAdjacentHTML('beforeend', htmlContent);
                    logEvent(analytics, EVENT_ADMIN_FLYOUT_OPENED, {
                        env: env,
                        timestamp: Date.now(),
                        analyticssessionid: analyticsSessionId,
                        sessionid: sessionId,
                        clientid: clientId
                    });
                }
            } else if (action === ACTION_CLOSE_QA) {
                const editorElement = document.getElementById('maison-qa-editor');
                qaEditorReady = false;
                if (editorElement) {
                    editorElement.remove();
                    document.getElementById('maison-chat-window')?.classList.remove('moved');
                    document.getElementById('maison-chat-icon')?.classList.remove('moved');
                    document.getElementById('maison-qa-editor')?.classList.remove('visible');
                    logEvent(analytics, EVENT_ADMIN_FLYOUT_CLOSED, {
                        env: env,
                        timestamp: Date.now(),
                        analyticssessionid: analyticsSessionId,
                        sessionid: sessionId,
                        clientid: clientId
                    });
                }
            }
            // Set the qa editor ready state flag
            else if (action === ACTION_QA_EDITOR_READY) {
                qaEditorReady = true;
                document.getElementById('maison-chat-window')?.classList.add('moved');
                document.getElementById('maison-chat-icon')?.classList.add('moved');
                document.getElementById('maison-qa-editor')?.classList.add('visible');
            }
            // Update the session auth status
            else if (action === ACTION_SIGNOUT || action === ACTION_AUTHENTICATED) {
                broadcastAuthStatusUpdate(action, data);
            }
            else if (action == ACTION_UPDATE_SESSION_ID) {
                sessionId = data.sessionId;
                // console.log(`Chat initated or resumed with session id ${sessionId}`);
                if (options.onSessionUpdate) {
                    options.onSessionUpdate(data.sessionId);    // This function can be used to track the session id of the bot
                }
            }
            else if (action == ACTION_UPDATE_LOCALE && options.onLocaleUpdate) {
                locale = data.locale;
                options.onLocaleUpdate(data.locale);
            }
            else if (action == EVENT_CLIENT_DATA_LOADED) {
                let iconPath = data.iconPath;
                setIconPath(iconPath);
            }
            else if (action == ACTION_EXECUTE_BOOK_ROOM) {
                if (options.onRequestBookRoom && typeof (options.onRequestBookRoom) == 'function') {
                    try {
                        options.onRequestBookRoom(data);
                    }
                    catch (err) {
                        // TODO: append message bubble with the error message received in this request.
                        // For now, do nothing.
                    }
                }
            }
            // Log the event to firebase analytics
            // Expect data in format { event: string, params: any }
            else if (action == ACTION_LOG_ANALYTICS_EVENT) {
                const { event, params } = JSON.parse(data);
                // console.log(`Logging analytics event: ${event}`);
                logEvent(analytics, event, {
                    env: env,
                    timestamp: Date.now(),
                    analyticssessionid: analyticsSessionId,
                    sessionid: sessionId,
                    clientid: clientId,
                    ...params,  // comes towards the end so any overrides are applied automatically.
                });
            }
        }
        catch (err) {
            // Do nothing. The message could be from a different source intended for some other listener.
            // MA-729 - if we have multiple GA trackers, then the event gets bubbled to all of these trackers including clients
            // It skews results a lot. If you want to see the events, do it on the dev envs. Log any messages to the console.
        }
    }

    window.addEventListener('message', processChatMessage, false);

    // Function shows the maison icon. Remember to add the client public assets to folder s3://{env}.maison.api/ca/{clientid}/{asset}
    // Create a folder for each client from it's client id
    function showIcon(): void {
        // If the chat icon is already injected, then do nothing
        if (document.getElementById('maison-chat-icon') != null) {
            return;
        }
        let style = '';
        if (options.iconPosition) {
            style = getIconStyle(options.iconPosition);
        }
        const htmlContent = `
        <div id="maison-chat-icon" class="visible" style="${style}">
            <div id="suggestion" ${translations?.values.invitationMessage ? '' : 'class="hidden"'} onclick="mcbs.hideSuggestion()">
                <span id="suggestion-text">${translations?.values.invitationMessage ? (translations?.values.invitationMessage).toString() : ''}</span>
                <div id="close-suggestion" style="background-image: url('${chatDomain}/assets/suggestion-close-icon.svg');"></div>
            </div>
            <div id="icon-container" onclick="mcbs.onMcbsIconClicked()">
                <div id="icon" style="background-image: url('${chatDomain}/ca/${clientId}/chat_open.svg');"></div>
            </div>
        </div>`;
        const maisonChatContainer = document.getElementById('maison-chat');
        if (!maisonChatContainer) {
            // Do nothing. Container is not there for icon to be injected.
            return;
        }
        maisonChatContainer!.insertAdjacentHTML('beforeend', htmlContent);
        if (options.iconPosition?.left != null) {
            document.querySelector('#maison-chat-icon #icon-container')?.classList.add('float-left');
        }
        else {
            document.querySelector('#maison-chat-icon #icon-container')?.classList.remove('float-left');
        }
        logEvent(analytics, EVENT_CHAT_ICON_INJECTED, {
            env: env,
            timestamp: Date.now(),
            analyticssessionid: analyticsSessionId,
            sessionid: sessionId,
            clientid: clientId
        });
    }
    function setIconPath(iconPath: string): void {
        if (iconPath) {
            document.getElementById('icon')?.setAttribute('style', `background-image: url('${chatDomain}${iconPath}')`)
        }
        document.getElementById('icon')?.classList.remove('hidden');
    }
    function hideIcon(): void {
        document.getElementById('maison-chat-icon')?.remove();
        logEvent(analytics, EVENT_CHAT_ICON_REMOVED, {
            env: env,
            timestamp: Date.now(),
            analyticssessionid: analyticsSessionId,
            sessionid: sessionId,
            clientid: clientId
        });
    }

    function updateIconPosition(iconPosition: IconPosition) {
        let style = getIconStyle(iconPosition);
        document.getElementById('maison-chat-icon')?.setAttribute('style', style);
        if (iconPosition.left != null) {
            // console.log('floating left');
            document.querySelector('#maison-chat-icon #icon-container')?.classList.add('float-left');
        }
        else {
            // console.log('removing float left');
            document.querySelector('#maison-chat-icon #icon-container')?.classList.remove('float-left');
        }
        logEvent(analytics, EVENT_CHAT_ICON_REPOSITIONED, {
            env: env,
            timestamp: Date.now(),
            analyticssessionid: analyticsSessionId,
            sessionid: sessionId,
            clientid: clientId
        });
    }

    function getIconStyle(iconPosition: IconPosition): string {
        let style = '';
        if (iconPosition.top) {
            style += `top:${iconPosition.top}px;`;
        }
        if (iconPosition.bottom) {
            style += `bottom: ${iconPosition.bottom}px;`;
        }
        if (iconPosition.left) {
            style += `left: ${iconPosition.left}px; right: unset;`;
        }
        if (iconPosition.right) {
            style += `right: ${iconPosition.right}px;`;
        }
        return style;
    }

    function updateLocale(updatedLocale: string): void {
        locale = updatedLocale;
        const chatElement = document.querySelector<HTMLIFrameElement>('#maison-chat iframe');
        if (chatElement) {
            chatElement.contentWindow?.postMessage(JSON.stringify({
                action: ACTION_UPDATE_LOCALE,
                data: { locale }
            }), chatDomain);
        }
    }

    function updateTranslations(trns: { translations: Translations }): void {
        if (!trns || !trns.translations || Object.keys(trns.translations.values).length == 0) {
            // Nothing to do
            return;
        }
        translations = translations || {
            default: trns.translations.default,
            systemKeys: trns.translations.systemKeys,
            values: {}
        };
        translations.systemKeys.push(...trns.translations.systemKeys.filter((tsk) => !translations.systemKeys.includes(tsk)));
        for (const [key, value] of Object.entries(trns.translations.values)) {
            translations.values[key] = value;
        }
    }

    Object.defineProperty(window, 'mcbs', {
        value: {
            onMcbsIconClicked,
            showChat,
            hideChat,
            hideSuggestion,
            showIcon,
            hideIcon,
            updateLocale,
            updateTranslations,
            updateIconPosition
        },
        writable: false,      // prevent reassignment of the Maison object
        configurable: false,  // prevent deletion or redefinition
        enumerable: true      // allow iteration
    });

    // Fetch translations through a script using call back to avoid CORS issues
    // Call this after the mcbs has initialised so the update translations method is available to use
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `${chatDomain}/api/translations?clientid=${options.clientId}&keys=invitationMessage&locales=${options.locale}&tcb=mcbs.updateTranslations`;
    document.head.appendChild(script);

    return window.mcbs;
}
